import React, { useCallback, useEffect, useRef, useState } from "react";
import { FieldProps, getIn, useFormikContext } from "formik";
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  CircularProgress,
  TextField,
  TextFieldProps,
} from "@mui/material";
import Text from "../base/Text";
import theme from "../../theme";
import { ExpandMore } from "@mui/icons-material";
import { useDispatch } from "../../store";
import {
  PlaceAddress,
  resolveSelectedAddress,
  searchAddressesTyped,
} from "../../places/addressSearch";
import { defaultParams } from "../../types/searchParams";
import { searchStates } from "../../slices/options";
import SearchResults from "../../types/searchResults";

interface Props {
  fieldName: string;
  placeId?: string;
}

export type LatLon = { lat: number; lon: number };

const FormikAddressSearch: React.FC<FieldProps & TextFieldProps & Props> = (
  props
) => {
  const {
    fieldName,
    placeId,
    form,
    helperText,
    field,
    error,
    ...rest
  } = props;

  const dispatch = useDispatch();

  const isTouched = getIn(form.touched, field.name);
  const errorMessage = getIn(form.errors, field.name);

  const { setFieldValue } = useFormikContext();

  const [open, setOpen] = useState(false);
  const [searchOpts, setSearchOpts] = useState({
    circleMeters: { longitude: -84.3885, latitude: 33.7501, radius: 20000 },
    max: 10,
  }); // default Atlanta
  const [options, setOptions] = useState<PlaceAddress[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<PlaceAddress | null>(
    null
  );
  const debounceRef = useRef<number | null>(null);
  const mapAddressRef = useRef<(full: PlaceAddress) => Promise<void>>();
  const valuesRef = useRef(form.values);

  const doSearch = useCallback(
    async (q: string) => {
      setLoading(true);

      try {
        const rows = await searchAddressesTyped(q, searchOpts);
        setOptions((prev) => {
          if (!selectedOption) return rows;
          const exists = rows.some(
            (option) => option.placeId === selectedOption.placeId
          );
          return exists ? rows : [selectedOption, ...rows];
        });
      } finally {
        setLoading(false);
      }
    },
    [searchOpts, selectedOption]
  );

  const handleInputChange = (
    _: any,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    if (value === "") {
      clearAddressFields();
    }
    setFieldValue(field.name, value);
    debounceRef.current = window.setTimeout(() => doSearch(value), 250);
  };

  const handleSelect = async (
    _: any,
    picked: PlaceAddress | null,
    reason?: AutocompleteChangeReason
  ) => {
    if (reason === "clear") {
      clearAddressFields();
      setSelectedOption(null);
      return;
    }

    if (!picked) return;

    const full = await resolveSelectedAddress(picked, searchOpts);
    if (!full) return;

    setSelectedOption(full);
    setOptions((prev) => {
      const exists = prev.some((option) => option.placeId === full.placeId);
      return exists ? prev : [full, ...prev];
    });

    await mapAddress(full);
  };

  const mapAddress = useCallback(
    async (full: PlaceAddress) => {
      setFieldValue(field.name, full.label ?? "");
      setFieldValue(`${fieldName}.line1`, full.line1);
      setFieldValue(`${fieldName}.line2`, full.line2);
      setFieldValue(`${fieldName}.city`, full.city);

      const stateSearch = {
        ...defaultParams,
        query: full.state,
      };

      const searchResults = (await dispatch(searchStates(stateSearch)))
        ?.payload as SearchResults;
      const state =
        searchResults?.totalResults === 1
          ? searchResults.results[0]
          : undefined;
      setFieldValue(`${fieldName}.usaState`, state);

      setFieldValue(`${fieldName}.zipCode`, full.zip);
      setFieldValue(`${fieldName}.placeId`, full.placeId);

      const currentName = getIn(valuesRef.current, "name");
      if (!currentName) {
        setFieldValue("name", full.label);
      }
    },
    [dispatch, field.name, fieldName, setFieldValue]
  );

  const clearAddressFields = useCallback(() => {
    setSelectedOption(null);
    setFieldValue(field.name, "");
    setFieldValue("name", "");
    setFieldValue(`${fieldName}.line1`, "");
    setFieldValue(`${fieldName}.line2`, "");
    setFieldValue(`${fieldName}.city`, "");
    setFieldValue(`${fieldName}.zipCode`, "");
    setFieldValue(`${fieldName}.usaState`, null);
    setFieldValue(`${fieldName}.placeId`, "");
  }, [field.name, fieldName, setFieldValue]);

  useEffect(() => {
    mapAddressRef.current = mapAddress;
  }, [mapAddress]);

  const defaultRenderOption = (
    props: React.HtmlHTMLAttributes<HTMLLIElement>,
    option: PlaceAddress
  ) => {
    const { key: optionKey, ...optionProps } = props as typeof props & {
      key?: React.Key;
    };
    return (
      <li
        key={optionKey}
        {...optionProps}
        style={{ backgroundColor: `${theme.palette.background.paper}` }}
      >
        {option?.label}
      </li>
    );
  };

  useEffect(() => {
    const getPos = async () => {
      if (typeof window === "undefined" || !("geolocation" in navigator))
        return;

      const timeoutMs = 2000;
      const maximumAge = 60_000;
      const enableHighAccuracy = true;

      try {
        const perm = await navigator.permissions?.query?.({
          name: "geolocation" as PermissionName,
        });
        if (perm && perm.state === "denied") return;

        navigator.geolocation.getCurrentPosition(
          (p) => {
            setSearchOpts({
              ...searchOpts,
              circleMeters: {
                longitude: p.coords.longitude,
                latitude: p.coords.latitude,
                radius: 20000,
              },
            });
          },
          () => {
            return;
          },
          { enableHighAccuracy, timeout: timeoutMs, maximumAge }
        );
      } catch {
        return;
      }
    };

    getPos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    valuesRef.current = form.values;
  }, [form.values]);

  useEffect(() => {
    if (!placeId) return;

    let isMounted = true;

    const getPlace = async () => {
      const full = await resolveSelectedAddress(
        {
          placeId,
          label: "",
          line1: "",
          line2: "",
          city: "",
          state: "",
          zip: "",
        },
        searchOpts
      );

      if (!full || !isMounted) return;

      setSelectedOption(full);
      setOptions((prev) => {
        const exists = prev.some((option) => option.placeId === full.placeId);
        return exists ? prev : [full, ...prev];
      });
      await mapAddressRef.current?.(full);
    };

    getPlace();

    return () => {
      isMounted = false;
    };
  }, [searchOpts, placeId]);

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedOption}
      inputValue={field.value ?? ""}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(o) => o?.label || ""}
      loading={loading}
      onChange={handleSelect}
      isOptionEqualToValue={(option, value) =>
        option.placeId === value?.placeId
      }
      filterOptions={(x) => x}
      noOptionsText="Search for an Address"
      popupIcon={<ExpandMore fontSize="medium" color="primary" />}
      renderOption={defaultRenderOption}
      renderInput={(params) => (
        <TextField
          {...params}
          helperText={
            helperText ?? (isTouched && errorMessage) ? (
              <Text text={errorMessage} variant="subtitle1" component="span" />
            ) : (
              " "
            )
          }
          {...rest}
          error={error ?? Boolean(isTouched && errorMessage)}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default FormikAddressSearch;
